import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import ReactDOM from "react-dom";

import {
  FiAlertCircle,
  FiBriefcase,
  FiCalendar,
  FiCheck,
  FiChevronDown,
  FiClock,
  FiFlag,
  FiSearch,
  FiTag,
  FiUser,
  FiUsers,
  FiX,
} from "react-icons/fi";

import API from "../../../../../utils/apiCalling";
import { config } from "../../../../../utils/apiUrl";
import useSwal from "../../../../../hooks/useSwal";

import "./AddEditTask.css";

const DEFAULT_STATUSES = [
  {
    value: "PENDING",
    label: "Pending",
  },
  {
    value: "IN_PROGRESS",
    label: "In Progress",
  },
  {
    value: "WAITING_FOLLOW_UP",
    label: "Waiting Follow-up",
  },
  {
    value: "ON_HOLD",
    label: "On Hold",
  },
  {
    value: "COMPLETED",
    label: "Completed",
  },
  {
    value: "CLOSED",
    label: "Closed",
  },
  {
    value: "CANCELLED",
    label: "Cancelled",
  },
];

const DEFAULT_PRIORITIES = [
  {
    value: "LOW",
    label: "Low",
  },
  {
    value: "MEDIUM",
    label: "Medium",
  },
  {
    value: "HIGH",
    label: "High",
  },
  {
    value: "URGENT",
    label: "Urgent",
  },
];

const DEFAULT_CATEGORIES = [
  {
    value: "GENERAL",
    label: "General",
  },
  {
    value: "CUSTOMER_FOLLOW_UP",
    label: "Customer Follow-up",
  },
  {
    value: "SALES",
    label: "Sales",
  },
  {
    value: "PURCHASE",
    label: "Purchase",
  },
  {
    value: "INVENTORY",
    label: "Inventory",
  },
  {
    value: "REPAIR_SERVICE",
    label: "Repair & Service",
  },
  {
    value: "DELIVERY",
    label: "Delivery",
  },
  {
    value: "PAYMENT_COLLECTION",
    label: "Payment Collection",
  },
  {
    value: "CCTV",
    label: "CCTV",
  },
  {
    value: "NETWORKING",
    label: "Networking",
  },
  {
    value: "WEBSITE",
    label: "Website",
  },
  {
    value: "MARKETING",
    label: "Marketing",
  },
  {
    value: "ADMINISTRATION",
    label: "Administration",
  },
];

const getTodayDate = () => {
  const date = new Date();

  const timezoneOffset =
    date.getTimezoneOffset() *
    60 *
    1000;

  return new Date(
    date.getTime() -
      timezoneOffset
  )
    .toISOString()
    .slice(0, 10);
};

const getDefaultDueDate = () => {
  const date = new Date();

  date.setDate(
    date.getDate() + 1
  );

  const timezoneOffset =
    date.getTimezoneOffset() *
    60 *
    1000;

  return new Date(
    date.getTime() -
      timezoneOffset
  )
    .toISOString()
    .slice(0, 10);
};

const formatDateInput = (
  value,
  includeTime = false
) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "";
  }

  const timezoneOffset =
    date.getTimezoneOffset() *
    60 *
    1000;

  const localDate = new Date(
    date.getTime() -
      timezoneOffset
  );

  return includeTime
    ? localDate
        .toISOString()
        .slice(0, 16)
    : localDate
        .toISOString()
        .slice(0, 10);
};

const normalizeOptions = (
  options,
  fallback
) => {
  if (
    Array.isArray(options) &&
    options.length
  ) {
    return options.map(
      (option) => {
        if (
          typeof option ===
          "string"
        ) {
          return {
            value: option,
            label: option,
          };
        }

        return {
          value:
            option?.value ||
            option?.key ||
            "",

          label:
            option?.label ||
            option?.name ||
            option?.value ||
            "",
        };
      }
    );
  }

  return fallback;
};

const mapRequirementCategory = (
  requirementType
) => {
  const value = String(
    requirementType || ""
  ).toLowerCase();

  if (
    value.includes("repair") ||
    value.includes("service") ||
    value.includes("laptop") ||
    value.includes("desktop") ||
    value.includes("printer") ||
    value.includes("scanner")
  ) {
    return "REPAIR_SERVICE";
  }

  if (value.includes("cctv")) {
    return "CCTV";
  }

  if (
    value.includes("network")
  ) {
    return "NETWORKING";
  }

  if (
    value.includes("website") ||
    value.includes("software")
  ) {
    return "WEBSITE";
  }

  return "CUSTOMER_FOLLOW_UP";
};

const INITIAL_FORM = {
  title: "",
  description: "",
  assignedTo: "",
  collaborators: [],
  status: "PENDING",
  priority: "MEDIUM",
  category: "GENERAL",
  startDate: getTodayDate(),
  dueDate: getDefaultDueDate(),
  followUpAt: "",
  estimatedHours: "",
  actualHours: "",
  progress: 0,
  tags: "",
  allowDuplicate: false,
};

const EmployeeSearchSelect = ({
  employees,
  selectedEmployee,
  search,
  setSearch,
  open,
  setOpen,
  loading,
  error,
  onSelect,
  disabled,
}) => {
  const containerRef =
    useRef(null);

  useEffect(() => {
    const handleOutsideClick = (
      event
    ) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(
          event.target
        )
      ) {
        setOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
    };
  }, [setOpen]);

  return (
    <div
      ref={containerRef}
      className="task-employee-select"
    >
      <button
        type="button"
        className={`task-employee-trigger ${
          error
            ? "has-error"
            : ""
        }`}
        onClick={() =>
          !disabled &&
          setOpen(
            (previous) =>
              !previous
          )
        }
        disabled={disabled}
      >
        {selectedEmployee ? (
          <div className="task-selected-employee">
            <span className="task-employee-avatar">
              {selectedEmployee
                ?.profileImage ? (
                <img
                  src={
                    selectedEmployee
                      .profileImage
                  }
                  alt={
                    selectedEmployee
                      .name
                  }
                />
              ) : (
                String(
                  selectedEmployee
                    ?.name || "E"
                )
                  .charAt(0)
                  .toUpperCase()
              )}
            </span>

            <div>
              <strong>
                {
                  selectedEmployee
                    ?.name
                }
              </strong>

              <small>
                {[
                  selectedEmployee
                    ?.employeeCode,
                  selectedEmployee
                    ?.department,
                  selectedEmployee
                    ?.designation,
                ]
                  .filter(Boolean)
                  .join(" • ")}
              </small>
            </div>
          </div>
        ) : (
          <div className="task-employee-placeholder">
            <FiUser />

            <span>
              Search and select employee
            </span>
          </div>
        )}

        <FiChevronDown
          className={
            open
              ? "task-select-arrow open"
              : "task-select-arrow"
          }
        />
      </button>

      {open ? (
        <div className="task-employee-dropdown">
          <div className="task-employee-search">
            <FiSearch />

            <input
              type="text"
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
              placeholder="Search by employee name, code, mobile..."
              autoFocus
            />
          </div>

          <div className="task-employee-options">
            {loading ? (
              <div className="task-employee-message">
                Loading employees...
              </div>
            ) : employees.length ===
              0 ? (
              <div className="task-employee-message">
                No active employee found.
              </div>
            ) : (
              employees.map(
                (employee) => (
                  <button
                    key={
                      employee._id
                    }
                    type="button"
                    className={`task-employee-option ${
                      String(
                        selectedEmployee?._id
                      ) ===
                      String(
                        employee._id
                      )
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => {
                      onSelect(
                        employee
                      );

                      setOpen(false);
                    }}
                  >
                    <span className="task-employee-avatar">
                      {employee
                        ?.profileImage ? (
                        <img
                          src={
                            employee
                              .profileImage
                          }
                          alt={
                            employee
                              .name
                          }
                        />
                      ) : (
                        String(
                          employee
                            ?.name ||
                            "E"
                        )
                          .charAt(0)
                          .toUpperCase()
                      )}
                    </span>

                    <div>
                      <strong>
                        {
                          employee.name
                        }
                      </strong>

                      <span>
                        {[
                          employee
                            .employeeCode,
                          employee
                            .department,
                          employee
                            .designation,
                        ]
                          .filter(
                            Boolean
                          )
                          .join(
                            " • "
                          )}
                      </span>

                      {employee
                        ?.mobile ? (
                        <small>
                          {
                            employee.mobile
                          }
                        </small>
                      ) : null}
                    </div>

                    {String(
                      selectedEmployee?._id
                    ) ===
                    String(
                      employee._id
                    ) ? (
                      <FiCheck />
                    ) : null}
                  </button>
                )
              )
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const AddEditTask = ({
  open,
  task = null,
  sourceContext = null,
  onClose,
  onSuccess,
}) => {
  const api = useMemo(
    () => new API(),
    []
  );

  const swal = useSwal();

  const debounceRef =
    useRef(null);

  const requestRef =
    useRef(0);

  const isEdit = Boolean(
    task?._id
  );

  const [form, setForm] =
    useState({
      ...INITIAL_FORM,
    });

  const [
    selectedEmployee,
    setSelectedEmployee,
  ] = useState(null);

  const [employees, setEmployees] =
    useState([]);

  const [
    employeeSearch,
    setEmployeeSearch,
  ] = useState("");

  const [
    employeeDropdownOpen,
    setEmployeeDropdownOpen,
  ] = useState(false);

  const [
    employeeLoading,
    setEmployeeLoading,
  ] = useState(false);

  const [
    assignmentAccess,
    setAssignmentAccess,
  ] = useState({
    fullAccess:
      true,

    assignmentLocked:
      false,

    roleCode:
      "",

    currentEmployeeId:
      null,

    currentEmployee:
      null,
  });

  const [
    dropdownData,
    setDropdownData,
  ] = useState({
    statuses:
      DEFAULT_STATUSES,
    priorities:
      DEFAULT_PRIORITIES,
    categories:
      DEFAULT_CATEGORIES,
    sourceTypes: [],
  });

  const [errors, setErrors] =
    useState({});

  const [submitting, setSubmitting] =
    useState(false);

  const [
    duplicateTask,
    setDuplicateTask,
  ] = useState(null);

  const isRequirementTask =
    sourceContext?.sourceType ===
      "CUSTOMER_REQUIREMENT" ||
    task?.source?.sourceType ===
      "CUSTOMER_REQUIREMENT";

  const fetchDropdownData =
    useCallback(async () => {
      try {
        const response =
          await api.get(
            config.getTaskDropdownData
          );

        if (
          response?.status ===
          "success"
        ) {
          setDropdownData({
            statuses:
              normalizeOptions(
                response?.data
                  ?.statuses,
                DEFAULT_STATUSES
              ),

            priorities:
              normalizeOptions(
                response?.data
                  ?.priorities,
                DEFAULT_PRIORITIES
              ),

            categories:
              normalizeOptions(
                response?.data
                  ?.categories,
                DEFAULT_CATEGORIES
              ),

            sourceTypes:
              normalizeOptions(
                response?.data
                  ?.sourceTypes,
                []
              ),
          });
        }
      } catch (errorResponse) {
        console.error(
          "Task dropdown error:",
          errorResponse
        );

        setDropdownData({
          statuses:
            DEFAULT_STATUSES,
          priorities:
            DEFAULT_PRIORITIES,
          categories:
            DEFAULT_CATEGORIES,
          sourceTypes: [],
        });
      }
    }, [api]);

  const fetchEmployees =
    useCallback(
      async (
        searchValue = ""
      ) => {
        const requestId =
          requestRef.current + 1;

        requestRef.current =
          requestId;

        try {
          setEmployeeLoading(
            true
          );

          const query =
            new URLSearchParams({
              search: String(
                searchValue || ""
              ).trim(),

              limit: "50",
            });

          const response =
            await api.get(
              `${config.getTaskEmployeeDropdown}?${query.toString()}`
            );

          if (
            requestId !==
            requestRef.current
          ) {
            return;
          }

          if (
            response?.status ===
            "success"
          ) {
            const nextEmployees =
              Array.isArray(
                response?.data
              )
                ? response.data
                : [];

            const nextAccess = {
              fullAccess:
                response?.access
                  ?.fullAccess !==
                false,

              assignmentLocked:
                Boolean(
                  response?.access
                    ?.assignmentLocked
                ),

              roleCode:
                String(
                  response?.access
                    ?.roleCode ||
                  ""
                )
                  .trim()
                  .toUpperCase(),

              currentEmployeeId:
                response?.access
                  ?.currentEmployeeId ||
                null,

              currentEmployee:
                response?.access
                  ?.currentEmployee ||
                null,
            };

            setEmployees(
              nextEmployees
            );

            setAssignmentAccess(
              nextAccess
            );

            if (
              nextAccess
                .assignmentLocked
            ) {
              const currentEmployee =
                nextEmployees.find(
                  (
                    employee
                  ) =>
                    String(
                      employee?._id ||
                      ""
                    ) ===
                    String(
                      nextAccess
                        .currentEmployeeId ||
                      ""
                    )
                ) ||
                nextAccess
                  .currentEmployee ||
                nextEmployees[0] ||
                null;

              setSelectedEmployee(
                currentEmployee
              );

              setForm(
                (
                  previous
                ) => ({
                  ...previous,

                  assignedTo:
                    currentEmployee
                      ?._id ||
                    nextAccess
                      .currentEmployeeId ||
                    "",
                })
              );

              setEmployeeDropdownOpen(
                false
              );
            }
          } else {
            setEmployees([]);
          }
        } catch (errorResponse) {
          if (
            requestId !==
            requestRef.current
          ) {
            return;
          }

          console.error(
            "Employee dropdown error:",
            errorResponse
          );

          setEmployees([]);
        } finally {
          if (
            requestId ===
            requestRef.current
          ) {
            setEmployeeLoading(
              false
            );
          }
        }
      },
      [api]
    );

  useEffect(() => {
    if (!open) {
      return;
    }

    fetchDropdownData();

    /*
    |--------------------------------------------------------------------------
    | Assignment access
    |--------------------------------------------------------------------------
    |
    | Restricted roles receive only their linked employee from the backend.
    | Load it immediately so Add and Edit can preselect the logged-in employee
    | even though the selector itself is disabled.
    |
    |--------------------------------------------------------------------------
    */

    fetchEmployees(
      ""
    );
  }, [
    open,
    fetchDropdownData,
    fetchEmployees,
  ]);

  useEffect(() => {
    if (
      !open ||
      !employeeDropdownOpen
    ) {
      return undefined;
    }

    if (
      debounceRef.current
    ) {
      clearTimeout(
        debounceRef.current
      );
    }

    debounceRef.current =
      setTimeout(() => {
        fetchEmployees(
          employeeSearch
        );
      }, 350);

    return () => {
      if (
        debounceRef.current
      ) {
        clearTimeout(
          debounceRef.current
        );
      }
    };
  }, [
    open,
    employeeDropdownOpen,
    employeeSearch,
    fetchEmployees,
  ]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (isEdit) {
      const assignedEmployee =
        task?.assignedTo &&
        typeof task.assignedTo ===
          "object"
          ? task.assignedTo
          : null;

      setSelectedEmployee(
        assignedEmployee
      );

      setForm({
        title:
          task?.title || "",

        description:
          task?.description || "",

        assignedTo:
          assignedEmployee?._id ||
          task?.assignedTo ||
          "",

        collaborators:
          Array.isArray(
            task?.collaborators
          )
            ? task.collaborators.map(
                (item) =>
                  item?._id ||
                  item
              )
            : [],

        status:
          task?.status ||
          "PENDING",

        priority:
          task?.priority ||
          "MEDIUM",

        category:
          task?.category ||
          "GENERAL",

        startDate:
          formatDateInput(
            task?.startDate
          ) || getTodayDate(),

        dueDate:
          formatDateInput(
            task?.dueDate
          ) ||
          getDefaultDueDate(),

        followUpAt:
          formatDateInput(
            task?.followUpAt,
            true
          ),

        estimatedHours:
          task?.estimatedHours ??
          "",

        actualHours:
          task?.actualHours ??
          "",

        progress:
          Number(
            task?.progress || 0
          ),

        tags:
          Array.isArray(
            task?.tags
          )
            ? task.tags.join(", ")
            : "",

        allowDuplicate: false,
      });
    } else {
      const requirementTitle =
        sourceContext
          ?.sourceTitle ||
        "";

      const customerName =
        sourceContext
          ?.customerName ||
        "";

      const generatedTitle =
        sourceContext?.sourceType ===
        "CUSTOMER_REQUIREMENT"
          ? `Follow up: ${requirementTitle}`
          : "";

      const generatedDescription =
        sourceContext?.sourceDescription ||
        (
          sourceContext?.sourceType ===
            "CUSTOMER_REQUIREMENT"
            ? `Follow up with ${
                customerName ||
                "customer"
              } regarding ${requirementTitle}.`
            : ""
        );

      setSelectedEmployee(null);

      setForm({
        ...INITIAL_FORM,

        startDate:
          getTodayDate(),

        dueDate:
          getDefaultDueDate(),

        title:
          generatedTitle,

        description:
          generatedDescription,

        category:
          sourceContext?.sourceType ===
          "CUSTOMER_REQUIREMENT"
            ? mapRequirementCategory(
                requirementTitle
              )
            : "GENERAL",
      });
    }

    setEmployeeSearch("");
    setEmployeeDropdownOpen(
      false
    );
    setEmployees([]);
    setErrors({});
    setSubmitting(false);
    setDuplicateTask(null);
  }, [
    open,
    task,
    isEdit,
    sourceContext,
  ]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    const handleEscape = (
      event
    ) => {
      if (
        event.key ===
          "Escape" &&
        !submitting
      ) {
        onClose?.();
      }
    };

    window.addEventListener(
      "keydown",
      handleEscape
    );

    return () => {
      document.body.style.overflow =
        previousOverflow;

      window.removeEventListener(
        "keydown",
        handleEscape
      );
    };
  }, [
    open,
    submitting,
    onClose,
  ]);

  const handleChange = (
    event
  ) => {
    const {
      name,
      value,
      type,
      checked,
    } = event.target;

    let updatedValue =
      type === "checkbox"
        ? checked
        : value;

    if (
      [
        "estimatedHours",
        "actualHours",
      ].includes(name)
    ) {
      updatedValue = value
        .replace(/[^\d.]/g, "")
        .replace(
          /(\..*)\./g,
          "$1"
        );
    }

    if (name === "progress") {
      updatedValue = Math.min(
        Math.max(
          Number(value),
          0
        ),
        100
      );
    }

    setForm((previous) => ({
      ...previous,
      [name]: updatedValue,
    }));

    setErrors((previous) => ({
      ...previous,
      [name]: "",
      form: "",
    }));

    setDuplicateTask(null);
  };

  const handleEmployeeSelect = (
    employee
  ) => {
    if (
      assignmentAccess
        .assignmentLocked
    ) {
      return;
    }

    setSelectedEmployee(
      employee
    );

    setForm((previous) => ({
      ...previous,
      assignedTo:
        employee?._id || "",
    }));

    setErrors((previous) => ({
      ...previous,
      assignedTo: "",
      form: "",
    }));

    setDuplicateTask(null);
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!form.title.trim()) {
      nextErrors.title =
        "Task title is required.";
    }

    if (
      form.title.trim().length >
      250
    ) {
      nextErrors.title =
        "Task title cannot exceed 250 characters.";
    }

    if (
      !form.description.trim()
    ) {
      nextErrors.description =
        "Task description is required.";
    }

    if (!form.assignedTo) {
      nextErrors.assignedTo =
        "Please select an employee.";
    }

    if (!form.startDate) {
      nextErrors.startDate =
        "Start date is required.";
    }

    if (!form.dueDate) {
      nextErrors.dueDate =
        "Due date is required.";
    }

    if (
      form.startDate &&
      form.dueDate &&
      new Date(form.dueDate) <
        new Date(form.startDate)
    ) {
      nextErrors.dueDate =
        "Due date cannot be earlier than start date.";
    }

    if (
      Number(form.progress) <
        0 ||
      Number(form.progress) >
        100
    ) {
      nextErrors.progress =
        "Progress must be between 0 and 100.";
    }

    setErrors(nextErrors);

    return (
      Object.keys(nextErrors)
        .length === 0
    );
  };

  const buildPayload = () => {
    const tags = form.tags
      .split(",")
      .map((item) =>
        item.trim()
      )
      .filter(Boolean);

    const source = sourceContext
      ? {
          sourceType:
            sourceContext
              .sourceType ||
            "GENERAL",

          sourceId:
            sourceContext
              .sourceId ||
            null,

          sourceCode:
            sourceContext
              .sourceCode ||
            "",

          sourceTitle:
            sourceContext
              .sourceTitle ||
            "",

          sourceDescription:
            sourceContext
              .sourceDescription ||
            "",

          sourceRoute:
            sourceContext
              .sourceRoute ||
            "",
        }
      : task?.source || {
          sourceType: "GENERAL",
        };

    const customerContext =
      sourceContext
        ? {
            customerId:
              sourceContext
                .customerId ||
              null,

            customerName:
              sourceContext
                .customerName ||
              "",

            mobile:
              sourceContext
                .customerMobile ||
              "",

            email:
              sourceContext
                .customerEmail ||
              "",

            companyName:
              sourceContext
                .companyName ||
              "",
          }
        : task?.customerContext ||
          {};

    return {
      title:
        form.title.trim(),

      description:
        form.description.trim(),

      assignedTo:
        form.assignedTo,

      collaborators:
        form.collaborators,

      status:
        form.status,

      priority:
        form.priority,

      category:
        form.category,

      startDate:
        form.startDate,

      dueDate:
        form.dueDate,

      followUpAt:
        form.followUpAt ||
        null,

      estimatedHours:
        Number(
          form.estimatedHours ||
            0
        ),

      actualHours:
        Number(
          form.actualHours ||
            0
        ),

      progress:
        Number(
          form.progress || 0
        ),

      tags,

      allowDuplicate:
        Boolean(
          form.allowDuplicate
        ),

      source,
      customerContext,
    };
  };

  const handleSubmit = async (
    event,
    forceDuplicate = false
  ) => {
    event?.preventDefault?.();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setErrors({});

      const payload =
        buildPayload();

      if (forceDuplicate) {
        payload.allowDuplicate =
          true;
      }

      const response = isEdit
        ? await api.put(
            config.updateTask(
              task._id
            ),
            payload
          )
        : await api.post(
            config.createTask,
            payload
          );

      if (
        response?.status ===
        "success"
      ) {
        await swal.success({
          icon: "success",
          title: isEdit
            ? "Task Updated"
            : "Task Assigned",

          text:
            response?.message ||
            (
              isEdit
                ? "Task updated successfully."
                : "Task created and assigned successfully."
            ),

          timer: 1500,
          showConfirmButton:
            false,
        });

        if (
          typeof onSuccess ===
          "function"
        ) {
          await onSuccess(
            response?.data ||
            null
          );
        }

        return;
      }

      throw new Error(
        response?.message ||
          "Unable to save task."
      );
    } catch (requestError) {
      console.error(
        "Save task error:",
        requestError
      );

      const responseData =
        requestError?.response
          ?.data ||
        requestError?.data ||
        {};

      if (
        Number(
          responseData?.code
        ) === 409 ||
        Number(
          requestError?.response
            ?.status
        ) === 409
      ) {
        setDuplicateTask(
          responseData?.data ||
            {
              existingTaskId:
                responseData
                  ?.existingTaskId,

              existingTaskCode:
                responseData
                  ?.existingTaskCode,

              existingTaskTitle:
                responseData
                  ?.existingTaskTitle,

              existingTaskStatus:
                responseData
                  ?.existingTaskStatus,
            }
        );

        setErrors({
          form:
            responseData?.message ||
            "An open task already exists for this requirement and employee.",
        });

        return;
      }

      const message =
        responseData?.message ||
        requestError?.message ||
        "Unable to save task.";

      setErrors({
        form: message,
      });

      swal.error({
        icon: "error",
        title: isEdit
          ? "Unable to update task"
          : "Unable to assign task",
        text: message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return null;
  }

  const statuses =
    dropdownData.statuses;

  const priorities =
    dropdownData.priorities;

  const categories =
    dropdownData.categories;

  return ReactDOM.createPortal(
    <div className="task-form-modal-root">
      <button
        type="button"
        className="task-form-backdrop"
        onClick={() => {
          if (!submitting) {
            onClose?.();
          }
        }}
        aria-label="Close task form"
      />

      <section
        className="task-form-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="task-form-title"
      >
        <header className="task-form-header">
          <div className="task-form-header-content">
            <span className="task-form-header-icon">
              <FiBriefcase />
            </span>

            <div>
              <small>
                TASK MANAGEMENT
              </small>

              <h2 id="task-form-title">
                {isEdit
                  ? "Edit Task"
                  : isRequirementTask
                    ? "Assign Requirement Task"
                    : "Create Task"}
              </h2>
            </div>
          </div>

          <button
            type="button"
            className="task-form-close"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
          >
            <FiX />
          </button>
        </header>

        <form
          className="task-form"
          onSubmit={handleSubmit}
        >
          <div className="task-form-body">
            {errors.form ? (
              <div className="task-form-error-banner">
                <FiAlertCircle />

                <span>
                  {errors.form}
                </span>
              </div>
            ) : null}

            {duplicateTask ? (
              <div className="task-duplicate-warning">
                <FiAlertCircle />

                <div>
                  <strong>
                    Existing open task found
                  </strong>

                  <span>
                    {[
                      duplicateTask
                        ?.existingTaskCode,
                      duplicateTask
                        ?.existingTaskTitle,
                      duplicateTask
                        ?.existingTaskStatus,
                    ]
                      .filter(Boolean)
                      .join(" • ")}
                  </span>

                  <p>
                    You may assign another task only when it is genuinely required.
                  </p>
                </div>
              </div>
            ) : null}

            {isRequirementTask ? (
              <section className="task-source-card">
                <div className="task-source-card-heading">
                  <span>
                    <FiTag />
                  </span>

                  <div>
                    <small>
                      LINKED CUSTOMER REQUIREMENT
                    </small>

                    <strong>
                      {sourceContext
                        ?.sourceTitle ||
                        task?.source
                          ?.sourceTitle ||
                        "Customer Requirement"}
                    </strong>
                  </div>
                </div>

                <div className="task-source-grid">
                  <div>
                    <span>
                      Requirement
                    </span>

                    <strong>
                      {sourceContext
                        ?.sourceCode ||
                        task?.source
                          ?.sourceCode ||
                        "-"}
                    </strong>
                  </div>

                  <div>
                    <span>
                      Customer
                    </span>

                    <strong>
                      {sourceContext
                        ?.customerName ||
                        task
                          ?.customerContext
                          ?.customerName ||
                        "-"}
                    </strong>
                  </div>

                  <div>
                    <span>
                      Mobile
                    </span>

                    <strong>
                      {sourceContext
                        ?.customerMobile ||
                        task
                          ?.customerContext
                          ?.mobile ||
                        "-"}
                    </strong>
                  </div>
                </div>

                {(sourceContext
                  ?.sourceDescription ||
                  task?.source
                    ?.sourceDescription) ? (
                  <p className="task-source-description">
                    {sourceContext
                      ?.sourceDescription ||
                      task?.source
                        ?.sourceDescription}
                  </p>
                ) : null}
              </section>
            ) : null}

            <section className="task-form-section">
              <div className="task-form-section-heading">
                <span>
                  <FiBriefcase />
                </span>

                <div>
                  <h3>
                    Task Information
                  </h3>

                  <p>
                    Add task title, instructions and category.
                  </p>
                </div>
              </div>

              <div className="task-form-grid">
                <div className="task-form-field task-form-full">
                  <label htmlFor="task-title">
                    Task Title *
                  </label>

                  <input
                    id="task-title"
                    type="text"
                    name="title"
                    value={form.title}
                    onChange={
                      handleChange
                    }
                    placeholder="Example: Follow up for laptop service"
                    maxLength={250}
                    disabled={submitting}
                  />

                  {errors.title ? (
                    <small className="task-field-error">
                      {errors.title}
                    </small>
                  ) : null}
                </div>

                <div className="task-form-field task-form-full">
                  <label htmlFor="task-description">
                    Description / Instructions *
                  </label>

                  <textarea
                    id="task-description"
                    name="description"
                    value={
                      form.description
                    }
                    onChange={
                      handleChange
                    }
                    placeholder="Explain what work needs to be completed..."
                    rows={5}
                    maxLength={10000}
                    disabled={submitting}
                  />

                  <div className="task-field-footer">
                    {errors.description ? (
                      <small className="task-field-error">
                        {
                          errors.description
                        }
                      </small>
                    ) : (
                      <span />
                    )}

                    <small>
                      {
                        form.description
                          .length
                      }
                      /10000
                    </small>
                  </div>
                </div>

                <div className="task-form-field">
                  <label htmlFor="task-category">
                    Category
                  </label>

                  <div className="task-select-wrapper">
                    <FiTag />

                    <select
                      id="task-category"
                      name="category"
                      value={
                        form.category
                      }
                      onChange={
                        handleChange
                      }
                      disabled={
                        submitting
                      }
                    >
                      {categories.map(
                        (option) => (
                          <option
                            key={
                              option.value
                            }
                            value={
                              option.value
                            }
                          >
                            {
                              option.label
                            }
                          </option>
                        )
                      )}
                    </select>
                  </div>
                </div>

                <div className="task-form-field">
                  <label htmlFor="task-priority">
                    Priority *
                  </label>

                  <div className="task-select-wrapper">
                    <FiFlag />

                    <select
                      id="task-priority"
                      name="priority"
                      value={
                        form.priority
                      }
                      onChange={
                        handleChange
                      }
                      disabled={
                        submitting
                      }
                    >
                      {priorities.map(
                        (option) => (
                          <option
                            key={
                              option.value
                            }
                            value={
                              option.value
                            }
                          >
                            {
                              option.label
                            }
                          </option>
                        )
                      )}
                    </select>
                  </div>
                </div>

                {isEdit ? (
                  <div className="task-form-field">
                    <label htmlFor="task-status">
                      Status
                    </label>

                    <select
                      id="task-status"
                      name="status"
                      value={
                        form.status
                      }
                      onChange={
                        handleChange
                      }
                      disabled={
                        submitting
                      }
                    >
                      {statuses.map(
                        (option) => (
                          <option
                            key={
                              option.value
                            }
                            value={
                              option.value
                            }
                          >
                            {
                              option.label
                            }
                          </option>
                        )
                      )}
                    </select>
                  </div>
                ) : null}
              </div>
            </section>

            <section className="task-form-section">
              <div className="task-form-section-heading">
                <span>
                  <FiUser />
                </span>

                <div>
                  <h3>
                    Assignment
                  </h3>

                  <p>
                    Select the employee responsible for this task.
                  </p>
                </div>
              </div>

              <div className="task-form-field">
                <label>
                  Assigned Employee *
                </label>

                <EmployeeSearchSelect
                  employees={employees}
                  selectedEmployee={
                    selectedEmployee
                  }
                  search={
                    employeeSearch
                  }
                  setSearch={
                    setEmployeeSearch
                  }
                  open={
                    employeeDropdownOpen
                  }
                  setOpen={
                    setEmployeeDropdownOpen
                  }
                  loading={
                    employeeLoading
                  }
                  error={
                    errors.assignedTo
                  }
                  onSelect={
                    handleEmployeeSelect
                  }
                  disabled={
                    submitting ||
                    assignmentAccess
                      .assignmentLocked
                  }
                />

                {errors.assignedTo ? (
                  <small className="task-field-error">
                    {
                      errors.assignedTo
                    }
                  </small>
                ) : (
                  <small className="task-field-help">
                    {assignmentAccess
                      .assignmentLocked
                      ? "Assigned to your logged-in employee profile."
                      : "Only active employees are displayed."}
                  </small>
                )}
              </div>
            </section>

            <section className="task-form-section">
              <div className="task-form-section-heading">
                <span>
                  <FiCalendar />
                </span>

                <div>
                  <h3>
                    Schedule & Follow-up
                  </h3>

                  <p>
                    Set task dates, follow-up and estimated work.
                  </p>
                </div>
              </div>

              <div className="task-form-grid task-form-date-grid">
                <div className="task-form-field">
                  <label htmlFor="task-start-date">
                    Start Date *
                  </label>

                  <div className="task-input-icon-wrapper">
                    <FiCalendar />

                    <input
                      id="task-start-date"
                      type="date"
                      name="startDate"
                      value={
                        form.startDate
                      }
                      onChange={
                        handleChange
                      }
                      disabled={
                        submitting
                      }
                    />
                  </div>

                  {errors.startDate ? (
                    <small className="task-field-error">
                      {
                        errors.startDate
                      }
                    </small>
                  ) : null}
                </div>

                <div className="task-form-field">
                  <label htmlFor="task-due-date">
                    Due Date *
                  </label>

                  <div className="task-input-icon-wrapper">
                    <FiCalendar />

                    <input
                      id="task-due-date"
                      type="date"
                      name="dueDate"
                      value={
                        form.dueDate
                      }
                      min={
                        form.startDate
                      }
                      onChange={
                        handleChange
                      }
                      disabled={
                        submitting
                      }
                    />
                  </div>

                  {errors.dueDate ? (
                    <small className="task-field-error">
                      {
                        errors.dueDate
                      }
                    </small>
                  ) : null}
                </div>

                <div className="task-form-field">
                  <label htmlFor="task-follow-up">
                    Follow-up Date & Time
                  </label>

                  <div className="task-input-icon-wrapper">
                    <FiClock />

                    <input
                      id="task-follow-up"
                      type="datetime-local"
                      name="followUpAt"
                      value={
                        form.followUpAt
                      }
                      onChange={
                        handleChange
                      }
                      disabled={
                        submitting
                      }
                    />
                  </div>
                </div>

                <div className="task-form-field">
                  <label htmlFor="task-estimated-hours">
                    Estimated Hours
                  </label>

                  <div className="task-input-icon-wrapper">
                    <FiClock />

                    <input
                      id="task-estimated-hours"
                      type="text"
                      inputMode="decimal"
                      name="estimatedHours"
                      value={
                        form.estimatedHours
                      }
                      onChange={
                        handleChange
                      }
                      placeholder="Example: 2.5"
                      disabled={
                        submitting
                      }
                    />
                  </div>
                </div>
              </div>
            </section>

            <section className="task-form-section">
              <div className="task-form-section-heading">
                <span>
                  <FiUsers />
                </span>

                <div>
                  <h3>
                    Additional Details
                  </h3>

                  <p>
                    Add tags and optional task information.
                  </p>
                </div>
              </div>

              <div className="task-form-grid">
                <div className="task-form-field task-form-full">
                  <label htmlFor="task-tags">
                    Tags
                  </label>

                  <div className="task-input-icon-wrapper">
                    <FiTag />

                    <input
                      id="task-tags"
                      type="text"
                      name="tags"
                      value={form.tags}
                      onChange={
                        handleChange
                      }
                      placeholder="repair, urgent customer, laptop"
                      disabled={
                        submitting
                      }
                    />
                  </div>

                  <small className="task-field-help">
                    Separate multiple tags with commas.
                  </small>
                </div>

                {isEdit ? (
                  <>
                    <div className="task-form-field">
                      <label htmlFor="task-actual-hours">
                        Actual Hours
                      </label>

                      <input
                        id="task-actual-hours"
                        type="text"
                        inputMode="decimal"
                        name="actualHours"
                        value={
                          form.actualHours
                        }
                        onChange={
                          handleChange
                        }
                        placeholder="0"
                        disabled={
                          submitting
                        }
                      />
                    </div>

                    <div className="task-form-field">
                      <label htmlFor="task-progress">
                        Progress:{" "}
                        {form.progress}%
                      </label>

                      <input
                        id="task-progress"
                        type="range"
                        name="progress"
                        value={
                          form.progress
                        }
                        min="0"
                        max="100"
                        step="5"
                        onChange={
                          handleChange
                        }
                        disabled={
                          submitting
                        }
                      />

                      <div className="task-progress-preview">
                        <span
                          style={{
                            width: `${form.progress}%`,
                          }}
                        />
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
            </section>

            {duplicateTask ? (
              <label className="task-duplicate-confirmation">
                <input
                  type="checkbox"
                  name="allowDuplicate"
                  checked={
                    form.allowDuplicate
                  }
                  onChange={
                    handleChange
                  }
                />

                <span>
                  <FiCheck />
                </span>

                <div>
                  <strong>
                    Create another task anyway
                  </strong>

                  <small>
                    I have reviewed the existing task and still need a separate assignment.
                  </small>
                </div>
              </label>
            ) : null}
          </div>

          <footer className="task-form-footer">
            <button
              type="button"
              className="task-form-cancel"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="task-form-submit"
              disabled={
                submitting ||
                (
                  duplicateTask &&
                  !form.allowDuplicate
                )
              }
              onClick={(event) => {
                if (
                  duplicateTask &&
                  form.allowDuplicate
                ) {
                  event.preventDefault();

                  handleSubmit(
                    event,
                    true
                  );
                }
              }}
            >
              {submitting
                ? "Please wait..."
                : isEdit
                  ? "Update Task"
                  : "Create & Assign Task"}
            </button>
          </footer>
        </form>
      </section>
    </div>,
    document.body
  );
};

export default AddEditTask;